import axios, {
  type AxiosError,
  type AxiosRequestHeaders,
  type InternalAxiosRequestConfig
} from 'axios';

import useLocalStorage from '~hooks/useLocalStorage';

// Variables to avoid multiple refresh requests
let isRefreshing = false;

interface FailedQueueItem {
  resolve: (token?: string | null) => void;
  reject: (err: unknown) => void;
}

interface RetryableRequestConfig {
  _retry?: boolean;
  _type?: 'private' | 'public';
  headers?: AxiosRequestHeaders;
}

let failedQueue: FailedQueueItem[] = [];

const processQueue = (error: unknown, token: string | null = null): void => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

const ensureHeaders = (config: RetryableRequestConfig) => {
  const headers = config.headers ?? {};
  config.headers = headers as AxiosRequestHeaders;
  return headers as AxiosRequestHeaders;
};

interface RegularData {
  code: string;
  detail: string;
}

interface DatatableData {
  data: RegularData;
  draw: number;
  recordsFiltered: number;
  recordsTotal: number;
}

// Configure axios interceptors
const setupAxiosInterceptors = () => {
  // Response interceptor
  axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as
        | RetryableRequestConfig
        | undefined;
      if (!originalRequest) return Promise.reject(error);

      const responseData = error.response?.data as
        | DatatableData
        | RegularData
        | undefined;
      let responseCode: string | undefined;
      if (responseData) {
        if ('code' in responseData) {
          responseCode = responseData.code;
        } else if ('data' in responseData && 'code' in responseData.data) {
          responseCode = responseData.data.code;
        }
      }

      if (
        !originalRequest._retry &&
        originalRequest._type !== 'public' &&
        responseCode === 'token_not_valid' &&
        error.response?.status === 401
      ) {
        if (isRefreshing) {
          // If already refreshing, add to the queue
          return new Promise<string | null | undefined>((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              if (token) {
                ensureHeaders(
                  originalRequest
                ).Authorization = `Bearer ${token}`;
              }
              return axios(originalRequest);
            })
            .catch((err) => {
              const rejection =
                err instanceof Error ? err : new Error(String(err));
              return Promise.reject(rejection);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const { userData, updateUserData } = useLocalStorage();
          const userRefresh = userData?.refresh;

          // Make refresh token request
          const refreshResponse = await axios.post<{ access: string }>(
            '/api/token/refresh/',
            { refresh: userRefresh },
            {
              baseURL: import.meta.env.VITE_BACKEND_URL,
              _retry: true
            } as unknown as InternalAxiosRequestConfig
          );
          const { access } = refreshResponse.data;

          // Update the token in localStorage
          updateUserData({ access });

          // Update the header of the original request
          ensureHeaders(originalRequest).Authorization = `Bearer ${access}`;

          // Process the failed requests queue
          processQueue(null, access);

          // Retry the original request
          return axios(originalRequest);
        } catch (refreshError) {
          // If the refresh fails, clear localStorage and redirect to login
          processQueue(refreshError, null);
          localStorage.clear();
          window.location.href = '/';
          const rejection =
            refreshError instanceof Error
              ? refreshError
              : new Error('Refresh token request failed');
          return Promise.reject(rejection);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );

  // Request interceptor to add default headers
  axios.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const { userData } = useLocalStorage();
      const userAccess = userData?.access;

      // Don't add authorization token for refresh-token requests
      if (config.url?.includes('/api/core/refresh-token/')) {
        // Explicitly remove Authorization header for refresh token requests
        const headers = ensureHeaders(config as RetryableRequestConfig);
        delete headers.Authorization;
        return config;
      }

      // Add authorization token if it exists for other requests
      if (userAccess) {
        const headers = ensureHeaders(config as RetryableRequestConfig);
        headers.Authorization ??= `Bearer ${userAccess}`;
      }
      return config;
    },
    (error: unknown) => {
      const rejection =
        error instanceof Error ? error : new Error(String(error));
      return Promise.reject(rejection);
    }
  );
};

// Initialize interceptors only once
let interceptorsInitialized = false;

const initializeAxiosInterceptors = () => {
  if (!interceptorsInitialized) {
    setupAxiosInterceptors();
    interceptorsInitialized = true;
  }
};

export { initializeAxiosInterceptors };
